import React, { useEffect, useRef, useState } from "react";

import type { ButtonType } from "@commonComponents/Button/Button";
import { css, styled } from "styled-components";

import { colorTheme, gridSpacing, favoriteObject } from "@utils";

import Button from "../Button/Button";
import ToolTip from "../ToolTip/ToolTip";
import type { NestedUserFavorite } from "~/src/types/__generated__/cc-api/api";

const FavoriteIcon = styled.i<{ $favorite: boolean }>`
  color: ${(props) =>
    props.$favorite
      ? colorTheme("warning")
      : colorTheme("neutralL3")} !important;

  ${({ $favorite }) =>
    $favorite &&
    css`
      &:hover {
        color: ${colorTheme("warningD2")} !important;
      }

      &:active {
        color: ${colorTheme("warningD1")} !important;
      }
    `}
  font-size: 19px;
  margin-left: ${gridSpacing[2]}px;
  cursor: pointer;
`;

type FavoriteButtonProps = {
  favorite: boolean;
  objectId: string;
  objectType: string;
  refreshFavorites: (params: {
    id: string;
    updatedFavorite: NestedUserFavorite | undefined;
  }) => void;
  style?: React.CSSProperties;
  useButton?: boolean;
  buttonType?: ButtonType;
  smallButton?: boolean;
};

const FavoriteButton = ({
  favorite,
  objectId,
  objectType,
  refreshFavorites,
  style,
  useButton,
  buttonType,
  smallButton = false,
}: FavoriteButtonProps) => {
  const [saving, setSaving] = useState(false);
  const resetSavingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  useEffect(
    () => () => {
      if (resetSavingTimeoutRef.current !== null) {
        clearTimeout(resetSavingTimeoutRef.current);
      }
    },
    [],
  );

  const updateFavorite = async (
    e: React.MouseEvent<HTMLElement, MouseEvent>,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    setSaving(true);

    const favRes = await favoriteObject({
      objectType,
      objectId,
      remove: favorite,
    });

    if (resetSavingTimeoutRef.current !== null) {
      clearTimeout(resetSavingTimeoutRef.current);
    }

    resetSavingTimeoutRef.current = setTimeout(() => {
      setSaving(false);
      resetSavingTimeoutRef.current = null;
    }, 150); // Timeout is for the spinner to show

    refreshFavorites({ id: objectId, updatedFavorite: favRes });
  };

  return (
    <ToolTip
      text={favorite ? "Unfavorite" : "Favorite"}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {useButton ? (
        <Button
          small={smallButton}
          style={{
            color: favorite ? colorTheme("warning") : colorTheme("neutralL1"),
            ...style,
          }}
          variant={buttonType}
          icon={
            saving
              ? "fa-solid fa-spinner-third fa-spin"
              : favorite
                ? "fa-solid fa-star"
                : "fa-regular fa-star"
          }
          disabled={saving}
          onClick={(e) => updateFavorite(e)}
          testId="favorite-button"
        />
      ) : (
        <FavoriteIcon
          style={style}
          className={
            saving
              ? "fa-solid fa-spinner-third fa-spin"
              : `fa-${favorite ? "solid" : "regular"} fa-star`
          }
          $favorite={favorite}
          onClick={(e) => updateFavorite(e)}
          data-testid="favorite-button-icon"
        />
      )}
    </ToolTip>
  );
};

export default FavoriteButton;
